// Manage Tags Modal
// Modal for managing available tags for downloads
// Allows adding and removing tags with validation

import React, { useState, useEffect } from 'react';
import { RevoltModal } from '../../../components/RevoltModal';
import { RevoltInput } from '../../../components/RevoltInput';
import { RevoltButton } from '../../../components/RevoltButton';
import './ManageTagsModal.css';

export function ManageTagsModal({ isOpen, onClose, onSubmit, tags: initialTags = [] }) {
  // Form state
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Reset and populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      setTags([...initialTags]);
      setNewTag('');
      setErrorMessage('');
    }
  }, [isOpen, initialTags]);

  const handleAddTag = () => {
    const tagValue = newTag.trim().toLowerCase();
    if (tagValue && !tags.includes(tagValue)) {
      setTags([...tags, tagValue].sort());
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Call the onSubmit callback with the tags
      if (onSubmit) {
        await onSubmit(tags);
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error('Error updating tags:', error);
      setErrorMessage(error.message || 'Failed to update tags. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RevoltModal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Tags"
      containerClassName="manage-tags-modal-container"
    >
      <div className="modal-scrollable-content">
        {errorMessage && (
          <div className="manage-tags-modal-error">
            <i className="fa-solid fa-exclamation-triangle"></i>
            {errorMessage}
          </div>
        )}
          <form onSubmit={handleSubmit} className="manage-tags-modal-form">
            <div className="manage-tags-modal-content">
              <div className="form-group">
                <label className="form-label">Current Tags</label>

                {/* Current Tags */}
                {tags.length > 0 ? (
                  <div className="tags-list">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => handleRemoveTag(tag)}
                          aria-label={`Remove ${tag} tag`}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="no-tags-message">No tags available yet.</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Add New Tag</label>

                {/* Add New Tag */}
                <div className="add-tag-section">
                  <RevoltInput
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Enter a new tag..."
                    maxLength={30}
                  />
                  <RevoltButton
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={handleAddTag}
                    disabled={!newTag.trim() || tags.includes(newTag.trim().toLowerCase())}
                  >
                    <i className="fa-solid fa-plus"></i>
                    Add
                  </RevoltButton>
                </div>
                <div className="form-help">
                  Tags help categorize downloads. Press Enter or click Add to add tags. Tags are automatically sorted and deduplicated.
                </div>
              </div>
            </div>
          </form>
      </div>

      <div className="manage-tags-modal-footer">
        <div className="modal-actions">
          <RevoltButton
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </RevoltButton>

          <RevoltButton
            variant="primary"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              'Save Tags'
            )}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}